import { useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import AccountsTable from "@/components/pipeline/AccountsTable";
import FunnelBlock from "@/components/pipeline/FunnelBlock";
import BDRTable from "@/components/pipeline/BDRTable";
import TimelinePanel from "@/components/pipeline/TimelinePanel";
import HealthInsights from "@/components/pipeline/HealthInsights";
import ManagementSummary from "@/components/pipeline/ManagementSummary";
import ActivityDialog from "@/components/pipeline/ActivityDialog";
import ScopeToggle from "@/components/pipeline/ScopeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { deleteActivity, getPipelineAnalytics, listActivities, listUsers } from "@/lib/activityApi";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription, hasCrmAccess } from "@/contexts/SubscriptionContext";
import { isQuotaError } from "@/lib/subscriptionApi";
import { Lock, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const SectionHeader = ({ index, label, sub }) => (
  <div className="flex items-baseline gap-3 mb-4 pb-2 border-b border-[#262626]">
    <span className="kpi-label text-[#525252]">/{String(index).padStart(2, "0")}</span>
    <h2 className="font-display text-xl md:text-2xl tracking-tight text-white">{label}</h2>
    {sub && <span className="kpi-label ml-auto">{sub}</span>}
  </div>
);

export default function PipelinePage() {
  const { user } = useAuth();
  const { data: sub, loading: subLoading } = useSubscription();
  const crmEnabled = hasCrmAccess(sub);
  const isManager = (user?.role || "bdr") === "manager";
  const [analytics, setAnalytics] = useState(null);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editInitial, setEditInitial] = useState(null);
  const [filter, setFilter] = useState("");

  // Scope: BDRs always see only their own data (server enforces anyway). Managers default to team view.
  const [scopeValue, setScopeValue] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = sessionStorage.getItem("radar_pipeline_scope");
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.warn("[pipeline] could not read scope from sessionStorage:", e?.message || e);
      }
    }
    return { scope: "team" };
  });

  useEffect(() => {
    try {
      sessionStorage.setItem("radar_pipeline_scope", JSON.stringify(scopeValue));
    } catch (e) {
      console.warn("[pipeline] could not persist scope to sessionStorage:", e?.message || e);
    }
  }, [scopeValue]);

  const queryParams = useMemo(() => {
    // BDRs: always force scope=mine (belt-and-suspenders; backend also enforces via role-agnostic scope handling)
    if (!isManager) return { scope: "mine" };
    if (scopeValue.scope === "mine") return { scope: "mine" };
    if (scopeValue.scope === "user" && scopeValue.user_id) return { user_id: scopeValue.user_id };
    return {};
  }, [scopeValue, isManager]);

  const load = async () => {
    if (!crmEnabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [acts, analytic, allUsers] = await Promise.all([
        listActivities(queryParams),
        getPipelineAnalytics(queryParams),
        users.length === 0 ? listUsers() : Promise.resolve(users),
      ]);
      setActivities(acts);
      setAnalytics(analytic);
      if (users.length === 0) setUsers(allUsers);
    } catch (err) {
      if (!isQuotaError(err)) {
        toast.error("Could not load pipeline", { description: err?.response?.data?.detail || err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [scopeValue, crmEnabled]);

  const onNew = () => {
    setEditInitial(null);
    setDialogOpen(true);
  };

  const onEdit = (id) => {
    const a = activities.find((x) => x.id === id);
    if (!a) return;
    setEditInitial(a);
    setDialogOpen(true);
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete activity for ${name}?`)) return;
    try {
      await deleteActivity(id);
      toast.success("Activity deleted");
      load();
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || err.message });
    }
  };

  const filteredAccounts = useMemo(() => {
    const rows = analytics?.section_a_accounts || [];
    if (!filter.trim()) return rows;
    const q = filter.toLowerCase();
    return rows.filter((r) => r.account_name.toLowerCase().includes(q) || r.bdr_name.toLowerCase().includes(q));
  }, [analytics, filter]);

  const hasData = activities.length > 0;

  // CRM Elite gate — render a polished paywall instead of the dashboard for Free/Pro users.
  if (!subLoading && !crmEnabled) {
    return (
      <div className="min-h-screen radar-grid-bg">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24 text-center" data-testid="pipeline-paywall">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 text-[#FF9F0A] text-[10px] font-mono-data tracking-wider uppercase mb-6">
            <Lock className="w-3 h-3"/> CRM Elite feature
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-white leading-[1.05]">
            The CRM Pipeline is part of <span className="text-[#FF9F0A]">CRM Elite</span>
          </h1>
          <p className="text-[#A3A3A3] mt-5 max-w-xl mx-auto text-[15px] leading-relaxed">
            Track every pitch, meeting and proposal — auto-derived stages, BDR scorecards, funnel
            analytics, stalled-deal alerts, and team rollup. Available exclusively on the CRM Elite plan.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link
              to="/pricing"
              data-testid="pipeline-paywall-cta"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-sm bg-[#FF9F0A] hover:bg-[#FFB340] text-[#0A0A0A] font-medium tracking-tight transition-colors"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.8}/>
              View CRM Elite plan
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-sm border border-[#262626] hover:border-[#404040] text-white transition-colors"
            >
              Back to dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen radar-grid-bg">
      <AppHeader
        rightSlot={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={load}
              data-testid="refresh-pipeline-btn"
              className="h-9 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040] gap-2 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/>
              <span className="text-xs font-mono-data tracking-wider uppercase">Refresh</span>
            </Button>
            <Button
              type="button"
              onClick={onNew}
              data-testid="new-activity-btn"
              className="h-9 px-4 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2 transition-colors"
            >
              <Plus className="w-3.5 h-3.5"/>
              <span className="text-xs font-mono-data tracking-wider uppercase">Log activity</span>
            </Button>
          </div>
        }
      />

      <main className="max-w-[1600px] mx-auto px-6 md:px-8 py-8 md:py-10 space-y-10">
        <section>
          <div className="kpi-label mb-3">/ BDR PRODUCTIVITY · PIPELINE PERFORMANCE</div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tighter text-white max-w-4xl leading-[1.05]">
            Track every pitch, meeting, proposal <span className="text-[#0A84FF]">without the CRM overhead.</span>
          </h1>
          <p className="text-[#A3A3A3] mt-4 max-w-2xl text-[15px] leading-relaxed">
            Log one activity per account. RADAR auto-derives stage, funnel conversions, BDR productivity,
            cycle times, and pipeline health — ready for your next weekly review.
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            {isManager ? (
              <>
                <ScopeToggle
                  value={scopeValue}
                  onChange={setScopeValue}
                  users={users}
                  currentUserName={user?.name}
                />
                <span className="kpi-label hidden md:inline" data-testid="scope-indicator">
                  {scopeValue.scope === "mine"
                    ? `Showing · ${user?.name || "my"} activity only`
                    : scopeValue.scope === "user"
                      ? `Showing · ${scopeValue.user_name || "BDR"} activity only`
                      : "Showing · team-wide"}
                </span>
              </>
            ) : (
              <span className="inline-flex items-center gap-2 kpi-label text-[#0A84FF]" data-testid="bdr-scope-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A84FF] animate-pulse"/>
                Your activity · {user?.name}
              </span>
            )}
          </div>
        </section>

        {hasData && analytics && isManager && (
          <section data-testid="section-f">
            <SectionHeader index="F" label="Management Summary" sub="Live"/>
            <ManagementSummary summary={analytics.section_f_summary}/>
          </section>
        )}

        <section data-testid="section-a">
          <SectionHeader index="A" label="Account Pipeline Tracker" sub={`${filteredAccounts.length} account${filteredAccounts.length === 1 ? "" : "s"}`}/>
          <div className="mb-3 flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]"/>
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by account or BDR…"
                className="pl-9 h-9 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm focus-visible:ring-1 focus-visible:ring-[#0A84FF]"
                data-testid="pipeline-filter-input"
              />
            </div>
            <div className="kpi-label hidden md:inline">Stage auto-derived</div>
          </div>
          <AccountsTable rows={filteredAccounts} onEdit={onEdit} onDelete={onDelete}/>
        </section>

        {hasData && analytics && (
          <>
            <section data-testid="section-b">
              <SectionHeader index="B" label="Activity Funnel Metrics"/>
              <FunnelBlock funnel={analytics.section_b_funnel}/>
            </section>

            <section data-testid="section-c">
              <SectionHeader index="C" label="BDR Performance Dashboard" sub={`${(analytics.section_c_bdrs || []).length} BDRs`}/>
              <BDRTable rows={isManager ? analytics.section_c_bdrs : (analytics.section_c_bdrs || []).filter((r) => r.bdr_name === user?.name)}/>
            </section>

            <section data-testid="section-d">
              <SectionHeader index="D" label="Timeline Tracking"/>
              <TimelinePanel
                timelines={analytics.section_d_timelines}
                cycleTimes={analytics.section_d_cycle_times}
              />
            </section>

            <section data-testid="section-e">
              <SectionHeader index="E" label="Pipeline Health Insights"/>
              <HealthInsights health={analytics.section_e_health}/>
            </section>
          </>
        )}

        {!hasData && !loading && (
          <div className="border border-dashed border-[#262626] rounded-sm p-12 text-center" data-testid="pipeline-empty-state">
            <div className="kpi-label mb-2 text-[#0A84FF]">/ EMPTY PIPELINE</div>
            <h3 className="font-display text-2xl text-white tracking-tight">Log your first activity</h3>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-md mx-auto">
              Capture pitches, meetings, proposals and wins for each account. RADAR builds the dashboard automatically.
            </p>
            <Button
              type="button"
              onClick={onNew}
              data-testid="empty-state-cta"
              className="mt-5 h-10 px-5 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2"
            >
              <Plus className="w-4 h-4"/>
              <span className="text-xs font-mono-data tracking-wider uppercase">Log activity</span>
            </Button>
          </div>
        )}

        <footer className="pt-8 border-t border-[#262626] flex items-center justify-between">
          <div className="kpi-label">/ RADAR · PIPELINE MODULE · {new Date().getFullYear()}</div>
          {analytics && (
            <div className="kpi-label">Generated {new Date(analytics.generated_at).toLocaleTimeString()}</div>
          )}
        </footer>
      </main>

      <ActivityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editInitial}
        onSaved={load}
      />
    </div>
  );
}
